/**
 * Minimum native app version (app_config key `min_native_version`), shaped
 * `{ "ios": "3.15.0" | null, "android": "4.2.0" | null }`. It ships null and
 * fails open: a missing row, a bad value, an unknown installed version or a
 * read error never blocks anyone. Set it only after the required binary is
 * live in that store.
 */
export const MIN_NATIVE_VERSION_KEY = 'min_native_version';

export interface MinNativeVersion {
  ios: string | null;
  android: string | null;
}

const VERSION = /^\d+(\.\d+){0,3}$/;

function cleanVersion(value: unknown): string | null {
  return typeof value === 'string' && VERSION.test(value.trim()) ? value.trim() : null;
}

/** app_config values arrive as jsonb objects or JSON strings. */
export function parseMinNativeVersion(value: unknown): MinNativeVersion {
  let raw = value;
  if (typeof raw === 'string') {
    try {
      raw = JSON.parse(raw);
    } catch {
      raw = null;
    }
  }
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return { ios: null, android: null };
  const record = raw as Record<string, unknown>;
  return { ios: cleanVersion(record.ios), android: cleanVersion(record.android) };
}

/** Negative when a < b, 0 when equal, positive when a > b ("3.14.9" < "3.14.10"). */
export function compareVersions(a: string, b: string): number {
  const left = a.split('.').map(Number);
  const right = b.split('.').map(Number);
  for (let i = 0; i < Math.max(left.length, right.length); i += 1) {
    const diff = (left[i] ?? 0) - (right[i] ?? 0);
    if (diff !== 0) return diff;
  }
  return 0;
}

/**
 * The marketing version from getAppVersion(), which on native appends the
 * build number ("3.14.25 (412)").
 */
export function installedMarketingVersion(installed: string | null | undefined): string | null {
  if (typeof installed !== 'string') return null;
  const match = /^\s*(\d+(?:\.\d+){0,3})(?:\s*\(.*\))?\s*$/.exec(installed);
  return match ? match[1] : null;
}

/** True only when both versions are known and the installed one is older. */
export function needsNativeUpdate(installed: string | null | undefined, minimum: string | null): boolean {
  const current = installedMarketingVersion(installed);
  if (!current || !minimum) return false;
  return compareVersions(current, minimum) < 0;
}
